import { useEffect, useState, type Dispatch, type SetStateAction } from "react";

import { parseCurrencyDollarsInput } from "../../../lib/financials";
import {
  SAVED_PROPERTY_STATUS_OPTIONS,
  SAVED_PROPERTY_TYPE_OPTIONS,
  type SavedPropertyStatus,
  type SavedPropertyType,
} from "../../../lib/properties";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import { Select } from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import { PropertyAddressAutocompleteField } from "./property-address-autocomplete";
import {
  estimateMonthlyCostDefaultsFromListPrice,
  monthlyCentsToFormValue,
  type PropertyFormState,
} from "./property-form.helpers";

export function PropertyFormFields({
  formState,
  includeSetAsTarget = true,
  setFormState,
}: {
  formState: PropertyFormState;
  includeSetAsTarget?: boolean;
  setFormState: Dispatch<SetStateAction<PropertyFormState>>;
}) {
  const [manualAddressFieldsOpen, setManualAddressFieldsOpen] = useState(false);

  useEffect(() => {
    const listPrice = parseCurrencyDollarsInput(formState.listPrice);
    const defaults = estimateMonthlyCostDefaultsFromListPrice(listPrice);
    if (!defaults || hasMonthlyFormValues(formState)) {
      return;
    }
    setFormState((current) => {
      if (
        hasMonthlyFormValues(current) ||
        current.listPrice !== formState.listPrice
      ) {
        return current;
      }
      return {
        ...current,
        estimatedPropertyTaxesMonthly: monthlyCentsToFormValue(
          defaults.estimatedPropertyTaxesMonthlyCents,
        ),
        estimatedHomeownersInsuranceMonthly: monthlyCentsToFormValue(
          defaults.estimatedHomeownersInsuranceMonthlyCents,
        ),
        estimatedHOAMonthly: monthlyCentsToFormValue(
          defaults.estimatedHOAMonthlyCents,
        ),
        costOverridesSource: defaults.costOverridesSource ?? "quote",
      };
    });
  }, [formState, setFormState]);

  const setMonthlyCostField = (
    field:
      | "estimatedHOAMonthly"
      | "estimatedHomeownersInsuranceMonthly"
      | "estimatedPropertyTaxesMonthly",
    value: string,
  ) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
      costOverridesSource: "user",
    }));
  };

  const setAddressField = (
    field: "addressLine1" | "addressLine2" | "city" | "postalCode" | "state",
    value: string,
  ) => {
    setFormState((current) => ({
      ...current,
      [field]: field === "state" ? value.toUpperCase() : value,
      selectedAddress: null,
    }));
  };

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2 grid gap-2 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <PropertyAddressAutocompleteField
              manualOverrideExpanded={manualAddressFieldsOpen}
              onManualOverrideChange={setManualAddressFieldsOpen}
              onSelectAddress={(selection) => {
                setManualAddressFieldsOpen(false);
                setFormState((current) => ({
                  ...current,
                  addressLine1: selection.addressLine1,
                  addressLine2: selection.addressLine2,
                  city: selection.city,
                  postalCode: selection.postalCode,
                  selectedAddress: selection,
                  state: selection.state,
                }));
              }}
              onValueChange={(value) => setAddressField("addressLine1", value)}
              selectedAddress={formState.selectedAddress}
              value={formState.addressLine1}
            />
          </div>
          {manualAddressFieldsOpen ? (
            <>
              <Input
                id="property-address-line-2"
                label="Unit / apt"
                onChange={(event) =>
                  setAddressField("addressLine2", event.target.value)
                }
                value={formState.addressLine2}
              />
              <Input
                id="property-city"
                label="City"
                onChange={(event) =>
                  setAddressField("city", event.target.value)
                }
                value={formState.city}
              />
              <Input
                id="property-state"
                label="State"
                maxLength={2}
                onChange={(event) =>
                  setAddressField("state", event.target.value)
                }
                value={formState.state}
              />
              <Input
                id="property-postal-code"
                label="ZIP code"
                onChange={(event) =>
                  setAddressField("postalCode", event.target.value)
                }
                value={formState.postalCode}
              />
            </>
          ) : null}
        </div>
        <Input
          id="property-list-price"
          label="List price"
          onChange={(event) =>
            setFormState((current) => ({
              ...current,
              listPrice: event.target.value,
            }))
          }
          required
          value={formState.listPrice}
        />
        <Select
          items={SAVED_PROPERTY_TYPE_OPTIONS}
          label="Property type"
          onValueChange={(value) =>
            setFormState((current) => ({
              ...current,
              propertyType: value as SavedPropertyType,
            }))
          }
          value={formState.propertyType}
        />
        <Select
          items={SAVED_PROPERTY_STATUS_OPTIONS}
          label="Status"
          onValueChange={(value) =>
            setFormState((current) => ({
              ...current,
              status: value as SavedPropertyStatus,
            }))
          }
          value={formState.status}
        />
      </div>

      <div className="mt-4 grid gap-4">
        <Input
          id="property-listing-url"
          label="Listing URL"
          onChange={(event) =>
            setFormState((current) => ({
              ...current,
              listingURL: event.target.value,
            }))
          }
          placeholder="https://"
          value={formState.listingURL}
        />
        <Input
          id="property-tags"
          label="Tags"
          onChange={(event) =>
            setFormState((current) => ({
              ...current,
              tags: event.target.value,
            }))
          }
          placeholder="walkable, needs work, quiet block"
          value={formState.tags}
        />
        <div className="rounded-2xl border border-border bg-muted/35 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm font-semibold text-foreground">
              Set monthly costs
            </p>
            <p className="text-xs text-muted-foreground">
              Taxes, insurance, HOA
            </p>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <Input
              id="property-taxes-monthly"
              label="Property taxes"
              onChange={(event) =>
                setMonthlyCostField(
                  "estimatedPropertyTaxesMonthly",
                  event.target.value,
                )
              }
              placeholder="0"
              value={formState.estimatedPropertyTaxesMonthly}
            />
            <Input
              id="property-insurance-monthly"
              label="Homeowners insurance"
              onChange={(event) =>
                setMonthlyCostField(
                  "estimatedHomeownersInsuranceMonthly",
                  event.target.value,
                )
              }
              placeholder="0"
              value={formState.estimatedHomeownersInsuranceMonthly}
            />
            <Input
              id="property-hoa-monthly"
              label="HOA / condo dues"
              onChange={(event) =>
                setMonthlyCostField("estimatedHOAMonthly", event.target.value)
              }
              placeholder="0"
              value={formState.estimatedHOAMonthly}
            />
          </div>
        </div>
        <Textarea
          id="property-notes"
          label="Notes"
          onChange={(event) =>
            setFormState((current) => ({
              ...current,
              notes: event.target.value,
            }))
          }
          placeholder="Why this one stands out, what you want to double-check, or anything to remember later."
          value={formState.notes}
        />
        <Checkbox
          checked={formState.favorite}
          description="Favorites stay pinned to the top of your list."
          label="Mark as favorite"
          onCheckedChange={(favorite) =>
            setFormState((current) => ({ ...current, favorite }))
          }
        />
        {includeSetAsTarget ? (
          <Checkbox
            checked={formState.setAsTarget}
            description="You will confirm before Sill uses this home for readiness and scenarios."
            label="Set as readiness target"
            onCheckedChange={(setAsTarget) =>
              setFormState((current) => ({ ...current, setAsTarget }))
            }
          />
        ) : null}
      </div>
    </>
  );
}

function hasMonthlyFormValues(formState: PropertyFormState) {
  return Boolean(
    formState.estimatedPropertyTaxesMonthly.trim() ||
    formState.estimatedHomeownersInsuranceMonthly.trim() ||
    formState.estimatedHOAMonthly.trim() ||
    formState.costOverridesSource,
  );
}
